import React, { Fragment } from "react";

import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import moment from "moment-timezone";

import formatAircraftSelection from "../../helpers/format-aircraft-selection";
import formatStage from "../../helpers/lead-stage-formatter";
import currencyFormatter from "../../helpers/currencyFormatter";
import { formatTripDate } from "../../helpers/dates";
import capitalizeFirstLetter from "../../helpers/capitalize-first-letter";
import getTripOptionOperatorPrice from "../../helpers/get-trip-option-operator-price";

import CheckBoxOutlineBlank from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";

import { Link } from "react-router-dom";

const getAircraftSelection = (data) => {
  if (data?.aircraftSelection?.length) {
    return data?.aircraftSelection
      .map((aircraft) => formatAircraftSelection(aircraft))
      .join(", ");
  }
  return "N/A";
};

function Row(props) {
  const { row, superAdminOrAd } = props;
  const [open, setOpen] = React.useState(false);

  // Check if it's a multileg trip
  const isMultileg = row.tripType === 3;

  console.log("ROW", row);

  return (
    <React.Fragment>
      <TableRow
        sx={{ "& > *": { borderBottom: "unset" } }}
        onClick={() => setOpen(!open)}
        className="fm-l2-parent-table-row"
      >
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell className="mobile-lead-details-initial-cell">
          <span className="material-icons lead-info-icon">person</span>
          {row.firstName} {row.lastName}
          <br />
          <span className="material-icons lead-info-icon">flight</span>
          {!isMultileg ? (
            <>
              {row?.itineraryDetails?.departureAirportCode} -{" "}
              {row?.itineraryDetails?.arrivalAirportCode}
            </>
          ) : (
            <>Multi-Leg Trip ({row?.itineraryDetails?.rawLegData?.length} legs)</>
          )}
          <br />
          <span className="material-icons lead-info-icon">date_range</span>
          {!isMultileg ? (
            <>
              {row?.itineraryDetails?.departureDate}
              {row.tripType === 2
                ? ` - ${row?.itineraryDetails?.returnDate}`
                : ""}
            </>
          ) : (
            <>
              {moment(
                row?.itineraryDetails?.rawLegData?.[0]?.departureDateString
              ).format("MMM D")}{" "}
              -{" "}
              {moment(
                row?.itineraryDetails?.rawLegData?.[row?.itineraryDetails?.rawLegData?.length - 1]
                  ?.departureDateString
              ).format("MMM D, YYYY")}
            </>
          )}
          <br />
          <span className="material-icons lead-info-icon">schedule</span>
          {formatStage(row.stage)}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }} className="fm-l2-child-table-container">
              <Typography
                variant="h5"
                gutterBottom
                component="div"
                className="fm-l2-child-table-heading mb-20 mt-20"
              >
                Lead Metadata
              </Typography>

              <p>
                <strong>Created At: </strong>
                {moment(row.createdAt).format("M/D/YY - h:mm a")}
              </p>

              <p>
                <strong>Stage: </strong>
                {formatStage(row.stage)}
              </p>

              <p>
                <strong>Assignee: </strong>
                {row.assigneeName || "-"}
              </p>

              <p>
                <strong>Organization: </strong>
                {row.organizationName}
              </p>

              <p>
                <strong>Lead ID: </strong>
                {row._id}
              </p>

              {(row.utmSource ||
                row.utmMedium ||
                row.utmCampaign ||
                row.utmTerm ||
                row.utmContent) && (
                <>
                  <p>
                    <strong>UTM Source: </strong>
                    {row.utmSource || "-"}
                  </p>

                  <p>
                    <strong>UTM Medium: </strong>
                    {row.utmMedium || "-"}
                  </p>

                  <p>
                    <strong>UTM Campaign: </strong>
                    {row.utmCampaign || "-"}
                  </p>

                  <p>
                    <strong>UTM Term: </strong>
                    {row.utmTerm || "-"}
                  </p>

                  <p>
                    <strong>UTM Content: </strong>
                    {row.utmContent || "-"}
                  </p>
                </>
              )}

              <hr></hr>

              <Typography
                variant="h5"
                gutterBottom
                component="div"
                className="fm-l2-child-table-heading mb-20 mt-20"
              >
                Quote Details
              </Typography>

              {row.quotes.length > 0 &&
                row.quotes.map((quote) => {
                  return (
                    <p key={quote}>
                      <Link to={`/quotes/${quote}`}>View Quote: {quote}</Link>
                    </p>
                  );
                })}

              {!row.quotes.length && (
                <p>
                  <strong>No Quotes Sent.</strong>
                </p>
              )}

              <hr></hr>

              <Typography
                variant="h5"
                gutterBottom
                component="div"
                className="fm-l2-child-table-heading mb-20 mt-20"
              >
                Trip Details
              </Typography>

              <p>
                <strong>Lead ID: </strong>
                {row._id}
              </p>

              <p>
                <strong>Trip Type: </strong>
                {row.tripType === 1
                  ? "One Way"
                  : row.tripType === 2
                  ? "Round Trip"
                  : "Multi-Leg"}
              </p>

              {!isMultileg ? (
                // Standard one-way or round trip display
                <>
                  <p>
                    <strong>Departure Date: </strong>
                    {row?.itineraryDetails?.departureDate}
                  </p>

                  {row.tripType === 2 && (
                    <p>
                      <strong>Return Date: </strong>
                      {row?.itineraryDetails?.returnDate || "N/A"}
                    </p>
                  )}

                  <p>
                    <strong>Departure Airport: </strong>
                    {row?.itineraryDetails?.departureAirportName}
                  </p>

                  <p>
                    <strong>Arrival Airport: </strong>
                    {row?.itineraryDetails?.arrivalAirportName}
                  </p>

                  <p>
                    <strong>Departure Time: </strong>
                    {row?.departureTime ? row?.departureTime : "N/A"}
                  </p>

                  {row.tripType === 2 && (
                    <p>
                      <strong>Return Time: </strong>
                      {row?.returnTime ? row?.returnTime : "N/A"}
                    </p>
                  )}
                </>
              ) : (
                // Multi-leg trip display
                <>
                  <p>
                    <strong>Total Distance: </strong>
                    {row?.itineraryDetails?.distance?.toFixed(2)} NM
                  </p>

                  <p>
                    <strong>Date Range: </strong>
                    {row?.itineraryDetails?.totalDateRange} days
                  </p>

                  <p>
                    <strong>Departure Time: </strong>
                    {row?.departureTime ? row?.departureTime : "N/A"}
                  </p>

                  <div style={{ marginTop: "15px" }}>
                    <strong>Legs:</strong>
                    {row?.itineraryDetails?.rawLegData?.map((leg, index) => (
                      <div
                        key={`leg-${index}`}
                        style={{
                          marginLeft: "10px",
                          marginTop: "10px",
                          borderLeft: "3px solid #ddd",
                          paddingLeft: "10px",
                        }}
                      >
                        <p style={{ margin: "5px 0", fontSize: "0.95em" }}>
                          <strong>Leg {index + 1}:</strong>{" "}
                          {leg.departureAirport?.city} (
                          {leg.departureAirport?.localCode}) →{" "}
                          {leg.arrivalAirport?.city} (
                          {leg.arrivalAirport?.localCode})
                        </p>
                        <p style={{ margin: "5px 0", fontSize: "0.85em" }}>
                          Date:{" "}
                          {formatTripDate(
                            leg.departureDateString,
                            "MMM Do YYYY"
                          )}{" "}
                          | Passengers: {leg.passengers}
                        </p>
                      </div>
                    ))}
                  </div>
                </>
              )}

              <hr></hr>

              <Typography
                variant="h5"
                gutterBottom
                component="div"
                className="fm-l2-child-table-heading mb-20 mt-20"
              >
                Additional Details
              </Typography>

              {!isMultileg ? (
                // Standard one-way or round trip passenger count
                <p>
                  <strong>Passengers: </strong>
                  {row?.itineraryDetails?.passengerCount}
                </p>
              ) : (
                // Multi-leg passenger breakdown by leg
                <div style={{ marginBottom: "10px" }}>
                  <strong>Passengers by Leg:</strong>
                  {row?.itineraryDetails?.rawLegData?.map((leg, index) => (
                    <p
                      key={`passengers-leg-${index}`}
                      style={{ margin: "5px 0", marginLeft: "20px" }}
                    >
                      <strong>Leg {index + 1}:</strong> {leg.passengers}{" "}
                      passenger{leg.passengers !== 1 ? "s" : ""}
                    </p>
                  ))}
                </div>
              )}

              <p>
                <strong>Aircraft Selection: </strong>
                {getAircraftSelection(row)}
              </p>

              {row?.leadOrigin === "iosApp" ? (
                row?.iosBaggage?.length &&
                row.iosBaggage.some((item) => item.bagType && item.quantity) ? (
                  <div>
                    <strong>Baggage: </strong>
                    <ul>
                      {row.iosBaggage.map((item, index) =>
                        item.bagType && item.quantity ? (
                          <li key={index}>
                            {item.bagType}: {item.quantity}
                          </li>
                        ) : null
                      )}
                    </ul>
                  </div>
                ) : (
                  <p>
                    <strong>Baggage: </strong>N/A
                  </p>
                )
              ) : (
                <p>
                  <strong>Baggage: </strong>
                  {row?.baggage?.length ? row.baggage.join(", ") : "N/A"}
                </p>
              )}

              <p>
                <strong>Pets: </strong>
                {row.pets ? "Yes" : "No"}
              </p>
              <p>
                <strong>Infants: </strong>
                {row.infants ? "Yes" : "No"}
              </p>

              <p>
                <strong>Comments: </strong>
                {row.comments}
              </p>

              <hr></hr>

              <Typography
                variant="h5"
                gutterBottom
                component="div"
                className="fm-l2-child-table-heading mb-20 mt-20"
              >
                Estimate Details
              </Typography>

              {!isMultileg ? (
                // Standard trip options display
                row?.tripOptions?.length > 0 &&
                row.tripOptions.map((option) => {
                  const {
                    aircraftName,
                    lowSpread,
                    highSpread,
                    finalPrice,
                  } = option;
                  const operatorPrice = getTripOptionOperatorPrice(
                    option,
                    row.tripType
                  );
                  return (
                    <div key={aircraftName} style={{ marginBottom: "14px" }}>
                      <p style={{ margin: "6px 0", textTransform: "uppercase" }}>
                        <strong>{aircraftName}</strong>
                      </p>
                      <p
                        style={{
                          marginTop: "6px",
                          marginBottom: "6px",
                          marginLeft: "10px",
                          fontSize: "1.05em",
                        }}
                      >
                        <strong>Estimate Range:</strong>{" "}
                        {currencyFormatter.format(lowSpread)} -{" "}
                        {currencyFormatter.format(highSpread)}
                      </p>
                      <p
                        style={{
                          marginTop: "6px",
                          marginBottom: "6px",
                          marginLeft: "10px",
                          fontSize: "1.05em",
                        }}
                      >
                        <strong>Calculator Price:</strong>{" "}
                        {finalPrice != null
                          ? currencyFormatter.format(finalPrice)
                          : "N/A"}
                      </p>
                      <p
                        style={{
                          marginTop: "6px",
                          marginBottom: "6px",
                          marginLeft: "10px",
                          fontSize: "1.05em",
                        }}
                      >
                        <strong>Expected Operator Price:</strong>{" "}
                        {operatorPrice != null
                          ? currencyFormatter.format(operatorPrice)
                          : "N/A"}
                      </p>
                    </div>
                  );
                })
              ) : // Multi-leg trip options display
              row?.tripOptions?.length > 0 ? (
                row.tripOptions.map(
                  (option, index) => {
                    return (
                      <div
                        key={`multileg-option-${index}`}
                        style={{
                          marginBottom: "15px",
                          padding: "10px",
                          backgroundColor: "#f5f5f5",
                          borderRadius: "4px",
                        }}
                      >
                        <p style={{ margin: "5px 0" }}>
                          <strong>
                            {option.aircraftOptionLabel || option.name}:
                          </strong>
                        </p>
                        <p
                          style={{
                            margin: "5px 0",
                            marginLeft: "10px",
                            fontSize: "0.9em",
                          }}
                        >
                          {option.commonlyAvailableLabel &&
                            `${option.commonlyAvailableLabel}`}
                        </p>
                        <p style={{ margin: "5px 0", marginLeft: "10px" }}>
                          <strong>Price:</strong>{" "}
                          {currencyFormatter.format(option.finalPrice)}
                        </p>
                        <p
                          style={{
                            margin: "5px 0",
                            marginLeft: "10px",
                            fontSize: "0.85em",
                          }}
                        >
                          Range: {currencyFormatter.format(option.lowSpread)} -{" "}
                          {currencyFormatter.format(option.highSpread)}
                          {option.fuelStops !== undefined &&
                            ` | Fuel Stops: ${option.fuelStops}`}
                        </p>
                      </div>
                    );
                  }
                )
              ) : (
                <p>No estimate data available.</p>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}

function SourcingRow(props) {
  const { superAdminOrAd } = props;

  let { row, leadData, dispatch, selectedRows, handleDelete, handleHide } =
    props;
  const [open, setOpen] = React.useState(false);

  if (!row) return null;

  let rowSelected = selectedRows.filter(
    (selectedRow) => row._id === selectedRow._id
  );

  rowSelected = rowSelected?.length ? true : false;

  // Check if lead is multileg for proper display
  const isMultileg = leadData.tripType === 3;

  console.log("SOURCING ROW", row);

  return (
    <>
      <TableRow
        sx={{ "& > *": { borderBottom: "unset" } }}
        onClick={() => setOpen(!open)}
        className={`fm-l2-parent-table-row ${
          row?.hidden ? "sourcing-record-hidden" : "sourcing-record-active"
        } ${
          row?.latestQuoteId && row?.latestQuoteTimeStamp
            ? "record-quote-sent"
            : row?.latestQuoteId
            ? "record-quote-draft"
            : "record-quote-null"
        }`}
        key={row._id}
      >
        <TableCell className="centered-important">
          <IconButton
            aria-label="expand row"
            size="small"
            className="sourcing-record-checkbox"
            disabled={row?.hidden}
            onClick={(e) => {
              e.stopPropagation();
              dispatch({
                type: "rowSelectionEvent",
                rowId: row._id,
                selected: !rowSelected,
              });
            }}
          >
            {rowSelected ? <CheckBoxIcon /> : <CheckBoxOutlineBlank />}
          </IconButton>
        </TableCell>

        <TableCell className="mobile-lead-details-initial-cell">
          <span className="material-icons lead-info-icon">person</span>
          {capitalizeFirstLetter(leadData.firstName)}{" "}
          {capitalizeFirstLetter(leadData.lastName)}
          <br />
          <span className="material-icons lead-info-icon">flight</span>
          {!isMultileg ? (
            <>
              {leadData.itineraryDetails.departureAirportCode} -{" "}
              {leadData.itineraryDetails.arrivalAirportCode}
            </>
          ) : (
            <>Multi-Leg ({leadData?.itineraryDetails?.rawLegData?.length} legs)</>
          )}
          <br />
          <span className="material-icons lead-info-icon">airlines</span>
          {row?.aircraft?.name}
          <br />
          <span className="material-icons lead-info-icon">payments</span>
          {row?.operatorPrice
            ? currencyFormatter.format(row.operatorPrice)
            : "-"}
          <br />
          <span className="material-icons lead-info-icon">credit_card</span>
          {row?.totalClientPayment
            ? currencyFormatter.format(row.totalClientPayment)
            : "-"}
        </TableCell>
      </TableRow>

      <TableRow
        className={`${
          row?.hidden ? "sourcing-record-hidden" : "sourcing-record-active"
        }`}
      >
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }} className="fm-l2-child-table-container">
              <Typography
                variant="h5"
                gutterBottom
                component="div"
                className="fm-l2-child-table-heading mb-20 mt-20"
              >
                Sourcing Record Data
              </Typography>

              <p key={`operatorName`}>
                <strong>Operator: </strong>
                {row?.operator?.name || "-"}
              </p>

              <p key={`tailNumber`}>
                <strong>Tail #: </strong>
                {row?.tailNumber || "-"}
              </p>

              <p key={`yom`}>
                <strong>YOM: </strong>
                {row?.yomIsRange && row?.yom && row?.yomEnd
                  ? `${row.yom} - ${row.yomEnd}`
                  : row?.yom
                  ? row.yom
                  : row?.fullAircraftData?.yom
                  ? row?.fullAircraftData?.yom
                  : "-"}
              </p>

              <p key={`interiorRefurb`}>
                <strong>Interior Refurb: </strong>
                {row?.interiorRefurbYearIsRange &&
                row?.interiorRefurbYear &&
                row?.interiorRefurbYearEnd
                  ? `${row.interiorRefurbYear} - ${row.interiorRefurbYearEnd}`
                  : row?.interiorRefurbYear}
              </p>

              <p key={`commission`}>
                <strong>Commission: </strong>
                {row?.grossCommission
                  ? currencyFormatter.format(row.grossCommission)
                  : "-"}
              </p>

              <p key={`createdAt-${row.createdAt}`}>
                <strong>Created At: </strong>
                {moment(row.createdAt).format("M/D/YY - h:mm a")}
              </p>

              <p key={`leadCreatedAt-${row.createdAt}`}>
                <strong>Lead Created At: </strong>
                {moment(leadData.createdAt).format("M/D/YY - h:mm a")}
              </p>

              <p className="mt-20">
                <Link
                  to={{
                    pathname: `/sourcing/${row?._id}`,
                    state: {
                      lead: props.leadData,
                    },
                  }}
                  className="evo-blue base-button"
                >
                  View / Edit Sourcing
                </Link>
              </p>

              <p className="mt-30">
                <Link
                  to={`/leads/${row?.leadId}`}
                  className="evo-gunmetal base-button"
                >
                  View Lead
                </Link>
              </p>

              <Typography
                variant="h5"
                gutterBottom
                component="div"
                className="fm-l2-child-table-heading mb-20 mt-20"
              >
                Actions
              </Typography>

              {!row?.hidden && (
                <button
                  className={`base-button btn-danger mr-20`}
                  key={"handle-delete"}
                  onClick={(e) => {
                    e.preventDefault();
                    handleDelete(row._id);
                  }}
                >
                  Delete
                </button>
              )}
              <button
                className={`base-button evo-gunmetal`}
                key={"handle-hide"}
                onClick={(e) => {
                  e.preventDefault();
                  handleHide(row._id);
                }}
              >
                {row?.hidden ? "Unhide" : "Hide"}
              </button>

              <Typography
                variant="h5"
                gutterBottom
                component="div"
                className="fm-l2-child-table-heading mb-20 mt-20"
              >
                Quote Status
              </Typography>

              {row?.latestQuoteId && row?.latestQuoteTimeStamp ? (
                <p>
                  <strong>Latest Quote Sent: </strong>
                  <Link to={`/quotes/${row?.latestQuoteId}`}>
                    {moment(row?.latestQuoteTimeStamp).format("lll")}
                  </Link>
                </p>
              ) : row?.latestQuoteId ? (
                <p>
                  <strong>Quote Drafted: </strong>
                  <Link to={`/quotes/${row?.latestQuoteId}`}>View Draft</Link>
                </p>
              ) : (
                "-"
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

function OperatorCallLogRow(props) {
  const { superAdminOrAd } = props;
  let { row, dispatch, handleDelete } = props;
  const [open, setOpen] = React.useState(false);

  if (!row) return null;

  return (
    <>
      <TableRow
        sx={{ "& > *": { borderBottom: "unset" } }}
        onClick={() => setOpen(!open)}
        className={`fm-l2-parent-table-row operator-call-log-row `}
        key={row._id}
      >
        <TableCell key="DETAILS" className="mobile-unavailable-table-cell">
          <p>
            <strong>Operator: </strong>
            {row.operator.name}
          </p>

          <p>
            <strong>Timestamp: </strong>
            {moment(row.timestamp).format("M/D/YY - h:mm a")}
          </p>
          <p>
            <strong>Notes: </strong>
            {row?.notes || "-"}
          </p>
          <button
            className={`base-button btn-danger mr-20 delete-icon`}
            key={"handle-delete"}
            onClick={(e) => {
              e.preventDefault();
              handleDelete(row._id);
            }}
          >
            <span className="material-icons">close</span>
          </button>
        </TableCell>
      </TableRow>
    </>
  );
}

export default function CollapsibleTable(props) {
  return (
    <>
      <h5 className="mt-30 mb-1">Lead Data</h5>

      <TableContainer
        component={Paper}
        className="accordion-table-with-details"
      >
        <Table aria-label="collapsible table">
          <TableHead className="fm-l2-parent-table-head">
            <TableRow className="fm-l2-parent-table-head-row">
              <TableCell id="leads-l2-mobile-header" colSpan={2}>
                LEAD INFO
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody className="fm-l2-parent-table-body">
            {props.rows.map((row) => (
              <Row key={row._id} row={row} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {props?.rows[0].sourcingRecords?.length > 0 && (
        <>
          <h2 className="mt-30 mb-0 text-center">SOURCING</h2>

          <TableContainer
            component={Paper}
            className="mt-30 accordion-table-with-details"
            key="sourcingTable"
          >
            <Table aria-label="collapsible table">
              <TableHead className="fm-l2-parent-table-head">
                <TableRow className="fm-l2-parent-table-head-row">
                  <TableCell />
                  <TableCell>RECORD DETAILS</TableCell>
                </TableRow>
              </TableHead>
              <TableBody className="fm-l2-parent-table-body">
                {props?.rows[0]?.sourcingRecords?.length > 0 &&
                  props?.rows[0]?.sourcingRecords.map((row) => (
                    <SourcingRow
                      key={row?._id}
                      row={row}
                      handleDelete={props.handleSourcingRecordDelete}
                      handleHide={props.handleSourcingRecordHide}
                      dispatch={props.parentDispatch}
                      selectedRows={props.selectedRows}
                      leadData={props.rows[0]}
                    />
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      <div className="text-right mt-20">
        <Link
          key="Add Record"
          className="base-button evo-blue"
          to={{ pathname: `/sourcing/new`, state: { lead: props.lead } }}
        >
          Add Record
        </Link>

        {props?.selectedRows?.length > 0 && (
          <Link
            key="Create Quote"
            className="base-button evo-gunmetal ml-20"
            to={{
              pathname: `/leads/${props?.lead?._id}/quotes/new`,
              state: {
                sourcingRecords: props.selectedRows,
                leadL2Data: props.lead,
              },
            }}
          >
            Create Quote
          </Link>
        )}
      </div>
    </>
  );
}
